"use client";

import { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import {MediaImage} from "@/components/templates/media/media--image";
import {DrupalMedia, DrupalTaxonomyTerm} from "next-drupal";
import {useRouter} from "next/router";

export default function TldrPodcast({
	title,
	description,
	coverImage,
	audioSource,
	category,
}: {
	title: string;
	description: string;
	coverImage: DrupalMedia;
	audioSource: string;
	category: DrupalTaxonomyTerm;
}) {
	const router = useRouter();
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const audioRef = useRef<HTMLAudioElement>(null);
	let showEnglishBadge = router?.locale !== router?.defaultLocale;


	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const updateTime = () => setCurrentTime(audio.currentTime);
		const updateDuration = () => {
			if (audio.duration !== Infinity) {
				setDuration(audio.duration);
			}
		};

		audio.addEventListener("timeupdate", updateTime);
		audio.addEventListener("loadedmetadata", updateDuration);
		audio.addEventListener("durationchange", updateDuration);

		// Force load metadata
		audio.load();

		return () => {
			audio.removeEventListener("timeupdate", updateTime);
			audio.removeEventListener("loadedmetadata", updateDuration);
			audio.removeEventListener("durationchange", updateDuration);
		};
	}, []);

	const togglePlay = () => {
		const audio = audioRef.current;
		if (!audio) return;

		if (isPlaying) {
			audio.pause();
		} else {
			audio.play();
		}
		setIsPlaying(!isPlaying);
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const audio = audioRef.current;
		if (!audio) return;

		const newTime = parseFloat(e.target.value);
		audio.currentTime = newTime;
		setCurrentTime(newTime);
	};

	const formatTime = (time: number) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	return (
		<div className="_component_bg w-full max-w-[900px] mx-auto my-6 p-0 overflow-hidden flex flex-col md:flex-row relative">
			<div className="relative w-full md:max-w-[15rem] h-auto md:min-h-[15rem]">
				<MediaImage
					loading={"lazy"}
					media={coverImage}
					sizes={"(max-width: 768px) 100vw, 240px"}
					alt="TL;DR Podcast Cover"
					className="w-full md:h-full relative md:max-w-[15rem] md:min-h-[15rem]"
					childClass="w-full h-48 md:h-full object-cover md:max-w-[15rem]"
				/>
				{showEnglishBadge && (
					<div className="absolute top-3 right-3 z-20 pointer-events-none">
						<span className="bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full">
							English
						</span>
					</div>
				)}
				<button
					onClick={togglePlay}
					className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 rounded-full w-20 h-20 flex items-center justify-center bg-white/30 backdrop-blur-sm hover:bg-primary/70 transition-all duration-200 ease-in-out hover:scale-110 border hover:border-2 hover:border-white"
				>
					{isPlaying ? (
						<Pause className="h-8 w-8 text-white" />
					) : (
						<Play className="h-8 w-8 text-white" />
					)}
				</button>
				<div className="absolute bottom-0 left-0 right-0 bg-black/50 px-3 pb-2 md:pt-2 z-20">
					<input
						type="range"
						min={0}
						max={duration || 100}
						value={currentTime}
						onChange={handleSeek}
						className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
					/>
					<div className="flex justify-between text-xs text-white mt-1">
						<span>{formatTime(currentTime)}</span>
						<span>{formatTime(duration)}</span>
					</div>
				</div>
			</div>
			<div className="p-8 pt-6 md:pt-8 my-auto">
				<h2 className="!text-sm pb-1">{ category?.name ? category.name : "TL;DR Podcast" }</h2>
				<h3 className="!text-primary font-semibold !pb-2 text-xl md:text-2xl">{title}</h3>
				<p className="leading-relaxed font-light">{description}</p>
			</div>
			<audio ref={audioRef} src={audioSource} preload="metadata" />
		</div>
	);
}
